import type { ContextMeaningOrigin, ContextMeaningPublicConfig } from '@orborix/shared-types';
import { fetchContextMeaning, type ContextMeaning } from '../../lib/contextMeaningApi.js';
import { POS_ES } from '../../lib/lexiconApi.js';
import type { WordLookupEntry, WordResolver } from './types.js';

/**
 * Resolver del «significado de esa frase» (encargo 49), para la burbuja del
 * coach cuando está prendido: tocar UNA palabra pregunta qué significado
 * revisado del diccionario tiene en esa oración (`POST /lexicon/meaning`), y
 * el popover muestra ése —no una traducción libre— con la opción de abrir la
 * ficha en él. Una FRASE confirmada (varias palabras) sigue con el lookup de
 * hoy (`fallback`).
 *
 * Cuando la palabra no está en el diccionario, con `missingWordDisplay =
 * 'auto-translation'` se agrega la traducción de hoy, rotulada como automática
 * (la decisión de #466 del 06/10; con `notice-only`, sólo el aviso).
 */

/** Dónde empieza la palabra número `at` de la línea (las mismas palabras que `wordList`: separadas por espacios). */
export function offsetOfWord(line: string, at: number): number {
  const re = /\S+/g;
  let i = 0;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    if (i === at) return m.index;
    i++;
  }
  throw new Error(`offsetOfWord: la línea no tiene la palabra ${at}`);
}

/** El resultado del backend, en lo que dibuja el popover. */
export function lookupEntryOf(r: ContextMeaning, auto: WordLookupEntry | null): WordLookupEntry {
  const empty = { translation: '', definition: null, partOfSpeech: null, examples: [] };
  switch (r.status) {
    case 'picked': {
      const m = r.entry.meanings?.find((x) => x.id === r.meaningId);
      if (!m) throw new Error(`context-meaning: ${r.meaningId} no está en la entrada ${r.word}`);
      return {
        translation: m.tr,
        definition: m.definition,
        partOfSpeech: m.pos,
        examples: [m.example],
        cefr: m.cefr,
        meaning: { kind: 'picked', entry: r.entry, meaningId: m.id },
      };
    }
    case 'unchosen':
      return {
        ...empty,
        meaning: { kind: 'unchosen', reason: r.reason, entry: r.entry, missing: r.missing },
      };
    case 'not-in-dictionary':
    case 'no-meanings':
      return {
        ...(auto ?? empty),
        meaning: {
          kind: 'missing-word',
          word: r.word,
          missing: r.missing,
          autoTranslation: auto !== null,
        },
      };
    case 'not-english':
      return { ...empty, meaning: { kind: 'not-english', word: r.word } };
    case 'provisional':
      return {
        translation: r.draft.clarification
          ? `${r.draft.translation} (${r.draft.clarification})`
          : r.draft.translation,
        definition: r.draft.definition,
        // El escritor lo da en inglés (`verb`); la tarjeta lo dice como el diccionario (`verbo`).
        partOfSpeech: POS_ES[r.draft.pos] ?? r.draft.pos,
        examples: [r.draft.example],
        meaning: { kind: 'provisional', word: r.word, draft: r.draft },
      };
  }
}

export interface MeaningResolverOptions {
  config: ContextMeaningPublicConfig;
  origin: ContextMeaningOrigin;
  /** El resolver de hoy: frases confirmadas y la traducción automática de lo que falta. */
  fallback: WordResolver;
  sessionId?: string;
}

const CACHE_MAX = 300;

export function makeMeaningResolver(opts: MeaningResolverOptions): WordResolver {
  // Por línea y posición (nunca por palabra sola): la misma palabra en otra
  // oración puede tener otro significado.
  const cache = new Map<string, WordLookupEntry>();
  const keyOf = (context: string, at: number) => `${at}@@${context}`;
  return {
    peekWord(word, context, at) {
      if (at === undefined) return opts.fallback.peekWord(word, context);
      return cache.get(keyOf(context, at));
    },
    async resolveWord(word, context, at) {
      if (at === undefined) return opts.fallback.resolveWord(word, context);
      const r = await fetchContextMeaning({
        text: word,
        sentence: context,
        offset: offsetOfWord(context, at),
        origin: opts.origin,
        lang: 'en',
        ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
      });
      const missingWord = r.status === 'not-in-dictionary' || r.status === 'no-meanings';
      const auto =
        missingWord && opts.config.missingWordDisplay === 'auto-translation'
          ? await opts.fallback.resolveWord(word, context).catch(() => null)
          : null;
      const entry = lookupEntryOf(r, auto);
      cache.set(keyOf(context, at), entry);
      if (cache.size > CACHE_MAX) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
      }
      return entry;
    },
  };
}
